import { dom, library } from '@fortawesome/fontawesome-svg-core';
import { faCogs, faKeyboard } from '@fortawesome/free-solid-svg-icons';
import _ from 'lodash';

import '../assets/scss/styles.scss';

import { disconnect } from './wetty/disconnect';
import { overlay } from './wetty/disconnect/elements';
import { verifyPrompt } from './wetty/disconnect/verify';
import { clientHandshake, encrypt, decrypt } from './wetty/crypto';
import { FileDownloader } from './wetty/download';
import { FlowControlClient } from './wetty/flowcontrol';
import { mobileKeyboard } from './wetty/mobile';
import { socket } from './wetty/socket';
import { terminal, Term } from './wetty/term';

// Setup for fontawesome
library.add(faCogs);
library.add(faKeyboard);
dom.watch();

function onResize(term: Term): () => void {
  return function resize() {
    term.resizeTerm();
  };
}

socket.on('connect', () => {
  const term = terminal(socket);
  if (_.isUndefined(term)) return;

  if (!_.isNull(overlay)) overlay.style.display = 'none';
  window.addEventListener('beforeunload', verifyPrompt, false);
  window.addEventListener('resize', onResize(term), false);

  term.resizeTerm();
  term.focus();
  mobileKeyboard();
  const fileDownloader = new FileDownloader();
  const fcClient = new FlowControlClient();

  // Resolves to the AES key once the handshake is done, or undefined if no encryption.
  // login handler awaits this so it never races with an in-progress handshake.
  let resolveEncKey!: (key: CryptoKey | undefined) => void;
  const encKeyPromise = new Promise<CryptoKey | undefined>(res => {
    resolveEncKey = res;
  });

  socket
    .on('encrypt', (serverJwk: JsonWebKey) => {
      clientHandshake(socket, serverJwk)
        .then(resolveEncKey)
        .catch((err: Error) => {
          if (!_.isUndefined(term))
            term.writeln(`\r\nEncryption error: ${err.message}`);
          socket.disconnect();
          resolveEncKey(undefined);
        });
    })
    .on('data', (payload: string) => {
      void (async () => {
        console.log('[client] data event received, payload type:', typeof payload, 'len:', (payload as string).length);
        const encKey = await encKeyPromise;
        console.log('[client] encKey available:', !!encKey);
        const data = encKey ? (await decrypt(encKey, payload)) as string : payload;
        console.log('[client] decrypted data len:', data.length);
        const remainingData = fileDownloader.buffer(data);
        const downloadLength = data.length - remainingData.length;
        if (downloadLength && fcClient.needsCommit(downloadLength)) {
          const commit = fcClient.ackBytes;
          if (encKey) {
            encrypt(encKey, commit).then(p => socket.emit('commit', p)).catch(() => {});
          } else {
            socket.emit('commit', commit);
          }
        }
        if (remainingData) {
          if (fcClient.needsCommit(remainingData.length)) {
            term.write(remainingData, () => {
              const commit = fcClient.ackBytes;
              if (encKey) {
                encrypt(encKey, commit).then(p => socket.emit('commit', p)).catch(() => {});
              } else {
                socket.emit('commit', commit);
              }
            });
          } else {
            term.write(remainingData);
          }
        }
      })();
    })
    .on('login', () => {
      // If no encrypt event arrived, resolveEncKey was never called — resolve with
      // undefined now so the promise settles and we proceed unencrypted.
      resolveEncKey(undefined);
      encKeyPromise.then(encKey => {
        if (_.isUndefined(term)) return;
        term.onData((data: string) => {
          if (encKey) {
            encrypt(encKey, data).then(p => socket.emit('input', p)).catch(() => {});
          } else {
            socket.emit('input', data);
          }
        });
        term.onResize((size: { cols: number; rows: number }) => {
          if (encKey) {
            encrypt(encKey, size).then(p => socket.emit('resize', p)).catch(() => {});
          } else {
            socket.emit('resize', size);
          }
        });
        term.writeln('');
        // Send initial size explicitly then fit — resizeTerm() no longer emits directly.
        const initialSize = { cols: term.cols, rows: term.rows };
        if (encKey) {
          encrypt(encKey, initialSize).then(p => socket.emit('resize', p)).catch(() => {});
        } else {
          socket.emit('resize', initialSize);
        }
        term.resizeTerm();
      }).catch(() => {});
    })
    .on('logout', disconnect)
    .on('disconnect', disconnect)
    .on('error', (err: string | null) => {
      if (err) disconnect(err);
    });
});
